import { useCallback, useRef, useState } from 'react';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { cartItemReferenceStoreAPI } from '../stores/cart-item-reference-store.public-api';
import {
    executeSyncToServer,
    getCartItemReferenceSyncStatus,
    setInternalUpdate,
    updateLastSyncedState,
} from '../stores/cart-item-reference.store';
import { fetchCartSyncReferences, reconcileWithServerReferences } from '../services/cart-sync-references.service';
import { CartItemReferenceSynchronizationService } from '../services/cart-item-reference-synchronization.service';
import type { CartItemReference } from '../models';
import type { CartItemInterface } from '../types/front.graphql';

/**
 * ============================================================================
 * useForceCartSync - Force synchronize local cart state with server
 * ============================================================================
 *
 * Use this hook before navigating to checkout to ensure cart data is
 * fully synchronized between local state and server.
 *
 * FLOW:
 * 1. If sync is in-progress, wait for it to complete
 * 2. If local has pending changes → force push to server via executeSyncToServer
 * 3. Fetch latest server state via fetchCartSyncReferences (server-authoritative)
 * 4. Reconcile server data with local state and update stores
 *
 * This guarantees that when the checkout page renders, it sees accurate
 * server-confirmed cart data (prices, items, promotions, coupons).
 *
 * @example
 * ```tsx
 * const { forceSync, isSyncing } = useForceCartSync();
 *
 * const handleNavigateToCheckout = async () => {
 *     await forceSync();
 *     router.push('/checkout');
 * };
 * ```
 */

/** Max time to wait for an in-progress sync to finish (ms) */
const SYNC_IN_PROGRESS_WAIT_MS = 10_000;
/** Poll interval while waiting for in-progress sync (ms) */
const SYNC_POLL_INTERVAL_MS = 200;

export const useForceCartSync = () => {
    const [isSyncing, setIsSyncing] = useState(false);
    const isSyncingRef = useRef(false);

    /**
     * Wait for any currently in-progress sync to complete.
     * Polls `CartItemReferenceSynchronizationService.isInProgress()` at intervals.
     * Returns immediately if no sync is running.
     */
    const waitForInProgressSync = useCallback((): Promise<void> => {
        if (!CartItemReferenceSynchronizationService.isInProgress()) {
            return Promise.resolve();
        }

        console.log('[useForceCartSync] Waiting for in-progress sync to complete...');

        return new Promise<void>(resolve => {
            const startTime = Date.now();

            const interval = setInterval(() => {
                if (!CartItemReferenceSynchronizationService.isInProgress()) {
                    clearInterval(interval);
                    console.log('[useForceCartSync] In-progress sync completed');
                    resolve();
                    return;
                }

                if (Date.now() - startTime > SYNC_IN_PROGRESS_WAIT_MS) {
                    clearInterval(interval);
                    console.warn('[useForceCartSync] Timed out waiting for in-progress sync');
                    resolve(); // Proceed anyway
                }
            }, SYNC_POLL_INTERVAL_MS);
        });
    }, []);

    /**
     * Force push pending local changes to server, then fetch fresh
     * server state and reconcile with local references.
     *
     * @returns Promise that resolves when sync is fully complete
     */
    const forceSync = useCallback(async (): Promise<void> => {
        // Prevent concurrent forceSync calls
        if (isSyncingRef.current) {
            console.warn('[useForceCartSync] forceSync already in progress, skipping');
            return;
        }

        const cartId = cartStoreAPI.state.getCartId();
        if (!cartId) {
            console.warn('[useForceCartSync] No cartId, skipping forceSync');
            return;
        }

        isSyncingRef.current = true;
        setIsSyncing(true);

        try {
            // ═══════════════════════════════════════════════════════════════════
            // STEP 1: Wait for any in-progress sync to finish
            // ═══════════════════════════════════════════════════════════════════
            await waitForInProgressSync();

            // ═══════════════════════════════════════════════════════════════════
            // STEP 2: Force push pending local changes to server
            // ═══════════════════════════════════════════════════════════════════
            const syncStatus = getCartItemReferenceSyncStatus();
            if (syncStatus.hasPendingChanges) {
                console.log('[useForceCartSync] Pushing pending local changes to server', {
                    pendingCount: syncStatus.pendingCount,
                });

                const currentItems = [...cartItemReferenceStoreAPI.state.getItems()] as CartItemReference[];
                await executeSyncToServer(currentItems);

                // Wait again in case the sync triggered a queue drain
                await waitForInProgressSync();
            } else {
                console.log('[useForceCartSync] No pending local changes, skipping push');
            }

            // ═══════════════════════════════════════════════════════════════════
            // STEP 3: Fetch fresh server state (server-authoritative)
            // ═══════════════════════════════════════════════════════════════════
            console.log('[useForceCartSync] Fetching fresh server state via cartSyncReferences');

            const syncReferencesResult = await fetchCartSyncReferences(cartId);

            if (!syncReferencesResult.success) {
                console.error('[useForceCartSync] Failed to fetch server references', syncReferencesResult.error);
                // Don't throw — the push already succeeded, checkout can still proceed
                return;
            }

            // ═══════════════════════════════════════════════════════════════════
            // STEP 4: Reconcile server data with local state
            // ═══════════════════════════════════════════════════════════════════
            const localReferences = [...cartItemReferenceStoreAPI.state.getItems()] as CartItemReference[];

            const reconciledReferences =
                syncReferencesResult.references.length > 0
                    ? reconcileWithServerReferences(syncReferencesResult.references, localReferences, cartId)
                    : [];

            console.log('[useForceCartSync] Reconciled references', {
                serverCount: syncReferencesResult.references.length,
                localCount: localReferences.length,
                reconciledCount: reconciledReferences.length,
            });

            // ═══════════════════════════════════════════════════════════════════
            // STEP 5: Update local stores (server-authoritative write)
            // ═══════════════════════════════════════════════════════════════════
            setInternalUpdate(true);
            try {
                cartItemReferenceStoreAPI.actions.setItems(reconciledReferences);
                updateLastSyncedState(reconciledReferences);
            } finally {
                setInternalUpdate(false);
            }

            // Update cartStore for backward compatibility
            const cartItems = reconciledReferences
                .map(ref => ref.cartItem)
                .filter((item): item is CartItemInterface => item != null);
            cartStoreAPI.actions.setItems(cartItems, cartId);

            console.log('[useForceCartSync] Force sync completed successfully');
        } catch (error) {
            console.error('[useForceCartSync] Error during force sync', error);
            // Don't rethrow — caller should still be able to navigate
        } finally {
            isSyncingRef.current = false;
            setIsSyncing(false);
        }
    }, [waitForInProgressSync]);

    return {
        /**
         * Force synchronize local cart with server.
         * Awaitable — resolves when sync is fully complete.
         */
        forceSync,

        /**
         * Whether a force sync is currently in progress.
         * Use this to show a loading indicator on the checkout button.
         */
        isSyncing,
    };
};
